import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { useT } from "../i18n";
import TwoFactorSetup from "./TwoFactorSetup";

/** Tarjeta de gestión de 2FA en el perfil: activar / desactivar. */
export default function TwoFactorCard() {
  const t = useT();
  const accessToken = useAuthStore(s => s.accessToken);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [orgRequires, setOrgRequires] = useState(false);
  const [mode, setMode] = useState<"view" | "setup" | "disable">("view");
  const [pwd, setPwd] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get<{ totp_enabled: boolean; org_requires_2fa: boolean }>("/auth/me")
    .then(d => { setEnabled(d.totp_enabled); setOrgRequires(d.org_requires_2fa); })
    .catch(() => {});

  useEffect(() => { load(); }, []);

  const disable = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/auth/2fa/disable", { current_password: pwd });
      setPwd(""); setMode("view"); setEnabled(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("twoFactor.error"));
    } finally { setBusy(false); }
  };

  const card: React.CSSProperties = {
    background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
    borderRadius: 16, padding: "1.5rem", marginBottom: "1.25rem",
  };
  const title: React.CSSProperties = { fontSize: "0.95rem", fontWeight: 700, color: "var(--clr-text)", marginBottom: "1rem" };

  return (
    <div style={card}>
      <div style={title}>{t("twoFactor.title")}</div>

      {enabled === null ? (
        <p style={{ color: "var(--clr-text3)", fontSize: "0.85rem" }}>{t("twoFactor.loading")}</p>
      ) : mode === "setup" ? (
        <TwoFactorSetup
          token={accessToken!}
          onDone={() => { setMode("view"); setEnabled(true); }}
          onCancel={() => setMode("view")}
        />
      ) : enabled ? (
        <div>
          <p style={{ fontSize: "0.88rem", color: "var(--clr-text2)", margin: "0 0 1rem" }}>
            <span style={{ color: "#16a34a", fontWeight: 700 }}>{t("twoFactor.enabledBadge")}</span>{" "}
            {t("twoFactor.enabledDesc")}
            {orgRequires && t("twoFactor.orgRequires")}
          </p>
          {!orgRequires && mode === "view" && (
            <button onClick={() => setMode("disable")}
              style={{ padding: "0.55rem 1.1rem", borderRadius: 10, border: "1px solid var(--clr-border)", background: "transparent", color: "var(--clr-text2)", cursor: "pointer", fontWeight: 600 }}>
              {t("twoFactor.disable")}
            </button>
          )}
          {!orgRequires && mode === "disable" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 320 }}>
              <input type="password" value={pwd} onChange={e => setPwd(e.target.value)} placeholder={t("twoFactor.currentPwdPlaceholder")}
                style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid var(--clr-border)" }} />
              {error && <div style={{ color: "#ef4444", fontSize: "0.82rem" }}>{error}</div>}
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => { setMode("view"); setError(""); setPwd(""); }}
                  style={{ padding: "0.55rem 1.1rem", borderRadius: 10, border: "1px solid var(--clr-border)", background: "transparent", cursor: "pointer" }}>{t("twoFactor.cancel")}</button>
                <button onClick={disable} disabled={busy || !pwd}
                  style={{ padding: "0.55rem 1.1rem", borderRadius: 10, border: "none", background: "#ef4444", color: "#fff", fontWeight: 600, cursor: "pointer", opacity: pwd ? 1 : 0.5 }}>
                  {busy ? "…" : t("twoFactor.confirmDisable")}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          <p style={{ fontSize: "0.88rem", color: "var(--clr-text2)", margin: "0 0 1rem" }}>
            {t("twoFactor.disabledDesc")}
          </p>
          <button onClick={() => setMode("setup")}
            style={{ padding: "0.55rem 1.1rem", borderRadius: 10, border: "none", background: "#0891b2", color: "#fff", cursor: "pointer", fontWeight: 600 }}>
            {t("twoFactor.enable")}
          </button>
        </div>
      )}
    </div>
  );
}
